import { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useTour } from "./TourContext";
import type { StepPlacement, TourStep } from "./types";

/**
 * Renders the spotlight + tooltip for the active tour step. Mounts as a
 * portal so it isn't constrained by the layout grid of the host page.
 *
 * Positioning strategy:
 *   - "center" placement → tooltip centered, no spotlight (intro / outro).
 *   - explicit placement (top/bottom/left/right) → tooltip placed relative
 *     to the target's bounding rect, falling back to "bottom" if that
 *     would clip the viewport.
 *   - target missing → centered, with a "Element not found" hint visible
 *     in the dev console (silent in prod) so step-author bugs surface
 *     without breaking the user's session.
 */
export function TourOverlay() {
  const { active, stepIndex, next, prev, cancel, stop } = useTour();
  const [rect, setRect] = useState<DOMRect | null>(null);

  const step: TourStep | null = active ? active.steps[stepIndex] ?? null : null;

  // Recompute target rect when step changes OR when the window resizes /
  // scrolls (page content shifts → spotlight needs to follow). useLayoutEffect
  // (not useEffect) so the spotlight + tooltip render before paint, no flicker.
  useLayoutEffect(() => {
    if (!step) {
      setRect(null);
      return;
    }
    if (!step.target || step.placement === "center") {
      setRect(null);
      return;
    }
    const el = document.querySelector(step.target);
    if (!el) {
      // eslint-disable-next-line no-console
      if (typeof process !== "undefined" && process.env?.NODE_ENV !== "production") {
        console.warn(`[tour] step target not found: ${step.target}`);
      }
      setRect(null);
      return;
    }
    const compute = () => setRect(el.getBoundingClientRect());
    compute();
    // Scroll the target into view so it's visible inside the spotlight.
    el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    const onResize = () => compute();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    // Re-measure once after scroll-into-view animation likely completes.
    const t = window.setTimeout(compute, 350);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
      window.clearTimeout(t);
    };
  }, [step]);

  if (!active || !step) return null;

  const isCentered = !step.target || step.placement === "center" || !rect;
  const isLast = stepIndex === active.steps.length - 1;
  const isFirst = stepIndex === 0;

  return createPortal(
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label={active.title}>
      {/* Backdrop. Click-through is disabled — the user must use the controls. */}
      <div
        className="absolute inset-0 bg-slate-900/55 backdrop-blur-[1px]"
        onClick={cancel}
      />

      {/* Spotlight cutout — a transparent rounded rectangle around the target,
          ringed with a soft outline so it pops against the dimmed backdrop. */}
      {!isCentered && rect && (
        <div
          className="pointer-events-none absolute rounded-lg ring-2 ring-white/95 shadow-[0_0_0_9999px_rgba(15,23,42,0.55)]"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            transition: "all 220ms cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        />
      )}

      {/* Tooltip. */}
      <Tooltip
        rect={rect}
        placement={step.placement}
        isCentered={isCentered}
      >
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-2xs font-semibold uppercase tracking-wider text-emerald-600">
              {active.title} · Step {stepIndex + 1} of {active.steps.length}
            </div>
            <div className="mt-1 text-base font-semibold text-slate-900">{step.title}</div>
            <div className="mt-1.5 text-sm leading-relaxed text-slate-700">{step.body}</div>
          </div>
          <button
            type="button"
            onClick={cancel}
            aria-label="Close tour"
            className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={stop}
            className="text-xs font-medium text-slate-500 hover:text-slate-800 hover:underline"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={prev}
              disabled={isFirst}
              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Back
            </button>
            <button
              type="button"
              onClick={next}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-700"
            >
              {isLast ? "Done" : "Next"} {!isLast && <ChevronRight className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>
      </Tooltip>
    </div>,
    document.body,
  );
}

const TOOLTIP_W = 360;
const TOOLTIP_GAP = 14;

function Tooltip({
  rect,
  placement,
  isCentered,
  children,
}: {
  rect: DOMRect | null;
  placement?: StepPlacement;
  isCentered: boolean;
  children: React.ReactNode;
}) {
  const [pos, setPos] = useState<{ top: number; left: number; arrow: StepPlacement | null }>({
    top: 0,
    left: 0,
    arrow: null,
  });

  // Recompute on every render where rect or placement changed. Cheap math —
  // single rect, no DOM reads beyond what useLayoutEffect already did above.
  useEffect(() => {
    if (isCentered || !rect) {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setPos({ top: Math.max(48, h / 2 - 120), left: Math.max(16, w / 2 - TOOLTIP_W / 2), arrow: null });
      return;
    }
    const w = window.innerWidth;
    const h = window.innerHeight;
    const tt = TOOLTIP_W;
    const ttGap = TOOLTIP_GAP;
    const place = placement ?? "bottom";

    let top = 0;
    let left = 0;
    let arrow: StepPlacement | null = place;

    const tryBottom = () => ({ top: rect.bottom + ttGap, left: rect.left + rect.width / 2 - tt / 2 });
    const tryTop = () => ({ top: rect.top - ttGap - 180, left: rect.left + rect.width / 2 - tt / 2 });
    const tryRight = () => ({ top: rect.top + rect.height / 2 - 90, left: rect.right + ttGap });
    const tryLeft = () => ({ top: rect.top + rect.height / 2 - 90, left: rect.left - ttGap - tt });

    let p = place === "top" ? tryTop() : place === "right" ? tryRight() : place === "left" ? tryLeft() : tryBottom();

    // Fallback chain when the chosen placement clips: bottom → top → right → left → centered.
    const clipsHoriz = (lp: { left: number }) => lp.left < 8 || lp.left + tt > w - 8;
    const clipsVert = (tp: { top: number }) => tp.top < 8 || tp.top + 180 > h - 8;

    if (clipsHoriz(p) || clipsVert(p)) {
      // try bottom first
      const candidates: Array<[StepPlacement, { top: number; left: number }]> = [
        ["bottom", tryBottom()],
        ["top", tryTop()],
        ["right", tryRight()],
        ["left", tryLeft()],
      ];
      const ok = candidates.find(([, c]) => !clipsHoriz(c) && !clipsVert(c));
      if (ok) {
        arrow = ok[0];
        p = ok[1];
      } else {
        arrow = null;
        p = { top: Math.max(48, h / 2 - 120), left: Math.max(16, w / 2 - tt / 2) };
      }
    }

    top = Math.max(8, Math.min(h - 188, p.top));
    left = Math.max(8, Math.min(w - tt - 8, p.left));
    setPos({ top, left, arrow });
  }, [rect, placement, isCentered]);

  return (
    <div
      role="tooltip"
      className="absolute w-[360px] rounded-xl border border-slate-200 bg-white p-4 shadow-xl ring-1 ring-slate-900/5"
      style={{ top: pos.top, left: pos.left, transition: "top 200ms, left 200ms" }}
    >
      {pos.arrow && <Arrow placement={pos.arrow} />}
      {children}
    </div>
  );
}

function Arrow({ placement }: { placement: StepPlacement }) {
  // CSS-borders-as-triangle. Color-matched to the tooltip background.
  const base = "absolute h-0 w-0";
  if (placement === "top") {
    return (
      <span
        className={`${base} bottom-[-8px] left-1/2 -translate-x-1/2 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white`}
      />
    );
  }
  if (placement === "right") {
    return (
      <span
        className={`${base} left-[-8px] top-1/2 -translate-y-1/2 border-b-8 border-r-8 border-t-8 border-b-transparent border-r-white border-t-transparent`}
      />
    );
  }
  if (placement === "left") {
    return (
      <span
        className={`${base} right-[-8px] top-1/2 -translate-y-1/2 border-b-8 border-l-8 border-t-8 border-b-transparent border-l-white border-t-transparent`}
      />
    );
  }
  // default = bottom
  return (
    <span
      className={`${base} top-[-8px] left-1/2 -translate-x-1/2 border-b-8 border-l-8 border-r-8 border-b-white border-l-transparent border-r-transparent`}
    />
  );
}
